var productName = 'calendar';import Base from '../../../Core/Base.js';
import Factoryable from '../../../Core/mixin/Factoryable.js';
import DomHelper from '../../../Core/helper/DomHelper.js';
/**
 * @module Calendar/layout/day/DayLayout
 */
/**
 * The item returned by the `layoutEvents` method.
 *
 * @typedef {Object} DayLayoutItem
 * @property {Scheduler.model.EventModel} eventRecord The `event` being displayed.
 * @property {DayLayoutCluster} cluster The cluster of events with overlapping times.
 * @property {Number} start The start time (as the number seconds since midnight) of the item.
 * @property {Number} end The end time (as the number seconds since midnight) of the item.
 * @property {Number} left The left position of the item as a percentage.
 * @property {Number} top The top position of the item as a percentage.
 * @property {Number} width The width of the item as a percentage.
 * @property {Number} height The height of the item as a percentage.
 * @property {Boolean} startsBefore Set to `true` if the event started in a previous day.
 * @property {Boolean} endsAfter Set to `true` if the event end in a future day.
 * @internal
 */
/**
 * A set of `items` that overlap. This object is passed to the `layoutCluster` method.
 *
 * @typedef {Object} DayLayoutCluster
 * @property {DayLayoutItem[]} items
 * @property {Number} start The start time (as the number seconds since midnight) of the item.
 * @property {Number} end The end time (as the number seconds since midnight) of the item.
 * @internal
 */
/**
 * The base class for {@link Calendar.widget.DayView DayView} layout algorithms.
 * @extends Core/Base
 * @abstract
 */
export default class DayLayout extends Base.mixin(Factoryable) {
    static get factoryable() {
        // establish this class as the Factoryable base
    }
    static get configurable() {
        return {
            /**
             * The minimum number of minutes an event must overlap another event before it is considered an overlap
             * for layout purposes.
             *
             * The default value of 0 treats any overlap in time as an overlap in the layout.
             * @config {Number}
             * @default 0
             * @internal
             */
            overlapTolerance : null
        };
    }
    /**
     * This method packages items into clusters of overlapping items.
     * @param {DayLayoutCluster[]} clusters The cluster array being produced. This method adds to this array.
     * @param {DayLayoutItem[]} items The items array being produced. This method adds `item` to this array.
     * @param {DayLayoutItem} item The item to add and pack into a cluster.
     * @private
     */
    clusterize(clusters, items, item) {
        const cluster = clusters[clusters.length - 1] || null;  // undefined => null when clusters.length=0
        items.push(item);
        // Because items are in order of increasing start times, we don't have to worry about coalescing clusters.
        if (cluster && this.overlaps(cluster, item)) {
            item.cluster = cluster;
            cluster.end = Math.max(cluster.end, item.end);
            cluster.items.push(item);
            // If the item overlaps a cluster, we simply add it there and return. No other items can overlap
            // since items are added in order of start time.
        }
        else {
            // Item does not overlap, so make a new cluster.
            clusters.push(item.cluster = {
                start : item.start,
                end   : item.end,
                items : [item]
            });
        }
    }
    /**
     * Calculate the placements for the given events within the specified time range.
     * @param {DayCell} cellData An object containing information about the day cell being created.
     * @returns {DayLayoutItem[]}
     * @internal
     */
    layoutEvents({ date, tomorrow, events, dayTime, denominator = 100, numerator = 100 * 100 }) {
        const
            me      = this,
            dayEnd  = dayTime.duration('s'),
            scaleFn = (v, d) => {
                v = Math.round(v * numerator) / denominator;
                return d ? `calc(${DomHelper.percentify(v)} ${d < 0 ? '-' : '+'} ${Math.abs(d)}px)` : v;
            },
            clusters = [],
            items = [];
        let i, item, n;
        // We need the events for the day to be sorted by startDate but limited to the start of the day. In other
        // words, all events that start before "midnight" are equally considered as starting at midnight. This order
        // of events is an assumption that runs deep in the FluidDayLayout but also in clusterization.
        events = dayTime.sortEvents(date, events.slice());
        // As the base class, we simply pick up the events and wrap them in a layout item to contain their positions:
        for (i = 0, n = events.length; i < n; i++) {
            item = me.createLayoutItem(dayTime, events[i], date, dayEnd, tomorrow);
            me.clusterize(clusters, items, item);
        }
        // cluster layouts are done in seconds:
        for (i = 0, n = clusters.length; i < n; ++i) {
            me.layoutCluster(clusters[i], scaleFn, dayTime);
        }
        // then scaled to percentages:
        for (i = 0, n = items.length; i < n; i++) {
            item = items[i];
            item.top = scaleFn(item.start / dayEnd);
            item.bottom = scaleFn(item.end / dayEnd);
            item.height = item.bottom - item.top;
        }
        return items;
    }
    /**
     * Returns the base of a {@link Calendar.layout.day.DayLayout#typedef-DayLayoutItem} for the passed
     * event record.
     * @param {Core.util.DayTime} dayTime The owning View's {@link Core.util.DayTime dayTime}
     * @param {Scheduler.model.EventModel} eventRecord The event record for which to create a layout item.
     * @param {Date} date A date from a {@link Calendar.widget.mixin.DayCellCollecter#typedef-DayCell}
     * which is correctly offset to reflect the current view's dayStartShift setting
     * @param {Number} dayEnd
     * @param {Date} tomorrow
     * @returns {DayLayoutItem}
     * @private
     */
    createLayoutItem(dayTime, eventRecord, date, dayEnd, tomorrow) {
        // Note: DH.diff() does not produce the correct layout position if DST adjustment lands on "date"
        const
            { allDay, startDate, endingDate } = eventRecord,
            start = (allDay || startDate < date) ? 0 : dayTime.delta(startDate, 's'),
            end   = (allDay || endingDate >= tomorrow) ? dayEnd : dayTime.delta(endingDate, 's');
        return {
            eventRecord,
            id           : eventRecord.id,
            start        : Math.max(start, 0),
            end          : Math.min(end, dayEnd),
            startsBefore : startDate < date || start < 0,
            endsAfter    : tomorrow < endingDate || end > dayEnd
        };
    }
    overlaps(a, b, tolerance, end) {  // minutes => seconds
        if (typeof tolerance === 'string') {
            end = tolerance;
            tolerance = null;
        }
        end = end || 'end';
        tolerance = tolerance ?? (this.overlapTolerance * 60 || 0); // avoid NaN
        return a.start < (b[end] - tolerance) && b.start < (a[end] - tolerance);
    }
}
DayLayout._$name = 'DayLayout';